import {
  createFileRoute,
  useNavigate,
  useParams,
} from '@tanstack/react-router';
import { Loader2, Shield } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import MinimalFooter from '@/components/common/MinimalFooter';
import MinimalHeader from '@/components/common/MinimalHeader';
import { useAdvisor } from '@/context/useAdvisorContext';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { getAdvisorPublicInfo } from '@/lib/auth/profile-service';

export const Route = createFileRoute('/_advisor/a/$username/')({
  component: AdvisorRedirectPage,
});

function AdvisorRedirectPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { username } = useParams({ from: '/_advisor/a/$username/' });
  const { setAdvisor } = useAdvisor();
  const isDesktop = useMediaQuery('(min-width: 768px)');

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchAdvisorAndRedirect() {
      setIsLoading(true);
      setError(null);

      const result = await getAdvisorPublicInfo(username);

      if (result.success && result.data) {
        setAdvisor(result.data);
        navigate({ to: '/form', replace: true });
      } else {
        setError(t('advisor.notFound'));
        setIsLoading(false);
      }
    }

    fetchAdvisorAndRedirect();
  }, [username, setAdvisor, navigate, t]);

  const handleReturnHome = () => {
    navigate({ to: '/' });
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen flex-col bg-surface-primary">
        <MinimalHeader showLanguageSwitcher={true} />
        <main className="flex flex-1 items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="h-8 w-8 animate-spin text-accent-primary" />
            <p className="text-foreground-secondary">{t('advisor.loading')}</p>
          </div>
        </main>
        <MinimalFooter variant={isDesktop ? 'desktop' : 'mobile'} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen flex-col bg-surface-primary">
        <MinimalHeader showLanguageSwitcher={true} />
        <main className="flex flex-1 items-center justify-center px-4">
          <div className="flex flex-col items-center gap-6 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
              <Shield className="h-8 w-8 text-red-500" />
            </div>
            <h1 className="font-heading text-2xl font-semibold text-foreground-primary">
              {t('advisor.notFound')}
            </h1>
            <p className="max-w-md text-foreground-secondary">
              {t('advisor.notFoundDescription')}
            </p>
            <button
              onClick={handleReturnHome}
              className="rounded-lg bg-accent-primary px-6 py-3 font-medium text-white transition-colors hover:bg-accent-hover"
            >
              {t('advisor.returnHome')}
            </button>
          </div>
        </main>
        <MinimalFooter variant={isDesktop ? 'desktop' : 'mobile'} />
      </div>
    );
  }

  return null;
}
